import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Alert, Spinner, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faEye, 
  faHeart, 
  faComment, 
  faShare, 
  faChartLine,
  faExclamationTriangle,
  faRefresh,
  faCalendarAlt,
  faGlobe,
  faMapMarkerAlt,
  faUserFriends,
  faThumbsUp,
  faComments,
  faShareAlt,
  faMousePointer,
  faNewspaper,
  faVideo,
  faImage,
  faHashtag,
  faLifeRing,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import { useAnalyticsCore } from '../../../cores/analytics';
import { useMeCore } from '../../../cores/me';
import { useGlobalCore } from '../../../cores/globals';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { FacebookErrorHandler } from './FacebookErrorHandler';
import { FacebookMetricsChart } from './FacebookMetricsChart';
import { FacebookLikesBalance } from './FacebookLikesBalance';
import { FacebookInteractions } from './FacebookInteractions';
import { FacebookPostsTable } from './FacebookPostsTable';
import './FacebookMetricsDashboard.scss';

interface MetricCard {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon: any;
  color: string;
  description?: string;
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    fill?: boolean;
  }[];
}

export const FacebookMetricsDashboard = () => {
  const intl = useIntl();
  const { 
    facebookInsights, 
    getFacebookInsights, 
    date, 
    updateDate,
    facebookErrorCode
  } = useAnalyticsCore();
  const { company, socialSettings } = useMeCore();
  const { isLoading } = useGlobalCore();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');

  // Verificar si Facebook está conectado
  const isFacebookConnected = useMemo(
    () => socialSettings?.facebook,
    [socialSettings]
  );

  // Cargar métricas cuando cambie el período
  useEffect(() => {
    if (isFacebookConnected && company?.id) {
      const endDate = moment().format('YYYY-MM-DD');
      const startDate = moment().subtract(
        selectedPeriod === '7d' ? 7 : selectedPeriod === '30d' ? 30 : 90, 
        'days'
      ).format('YYYY-MM-DD');
      
      updateDate({ since: startDate, until: endDate });
      getFacebookInsights(company.id, startDate, endDate, 'Daily');
    }
  }, [isFacebookConnected, company, selectedPeriod, getFacebookInsights, updateDate]);

  // Métricas principales
  const mainMetrics: MetricCard[] = useMemo(() => {
    if (!facebookInsights) return [];

    return [
      {
        title: 'Me gusta',
        value: Number(facebookInsights.page_fans?.values?.[0]?.value) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faThumbsUp,
        color: '#1877F2',
        description: 'Total de me gusta en la página'
      },
      {
        title: 'Seguidores',
        value: Number(facebookInsights.page_fans?.values?.[0]?.value) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faUsers,
        color: '#42B883',
        description: 'Total de seguidores'
      },
      {
        title: 'Impresiones',
        value: Number(facebookInsights.page_impressions?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faEye,
        color: '#E4405F',
        description: 'Total de impresiones'
      },
      {
        title: 'Visitas a página',
        value: Number(facebookInsights.page_views_total?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faMousePointer,
        color: '#8B5CF6',
        description: 'Total de visitas a la página'
      },
      {
        title: 'Contenido total',
        value: facebookInsights.page_posts?.values?.length || 0,
        change: 0,
        changeType: 'neutral',
        icon: faNewspaper,
        color: '#F59E0B',
        description: 'Total de publicaciones'
      }
    ];
  }, [facebookInsights]);

  // Métricas de interacciones
  const interactionMetrics: MetricCard[] = useMemo(() => {
    if (!facebookInsights) return [];

    return [
      {
        title: 'Reacciones',
        value: Number(facebookInsights.page_positive_feedback_by_type?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faHeart,
        color: '#EC4899',
        description: 'Total de reacciones'
      },
      {
        title: 'Comentarios',
        value: Number(facebookInsights.page_post_engagements?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faComments,
        color: '#10B981',
        description: 'Total de comentarios'
      },
      {
        title: 'Compartidos',
        value: Number(facebookInsights.page_post_engagements?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faShareAlt,
        color: '#F97316',
        description: 'Total de compartidos'
      },
      {
        title: 'Clics',
        value: Number(facebookInsights.page_total_actions?.values?.reduce((a: number, b: any) => a + (b?.value || 0), 0)) || 0,
        change: 0,
        changeType: 'neutral',
        icon: faMousePointer,
        color: '#8B5CF6',
        description: 'Total de clics'
      },
      {
        title: 'Posts',
        value: facebookInsights.page_posts?.values?.length || 0,
        change: 0,
        changeType: 'neutral',
        icon: faNewspaper,
        color: '#6366F1',
        description: 'Total de posts'
      }
    ];
  }, [facebookInsights]);

  // Datos para gráficos
  const chartData: ChartData = useMemo(() => {
    if (!facebookInsights?.page_impressions?.values) return { labels: [], datasets: [] };

    const labels = facebookInsights.page_impressions.values.map((_: any, index: number) => {
      const date = moment().subtract(facebookInsights.page_impressions.values.length - index - 1, 'days');
      return date.format('DD MMM');
    });

    return {
      labels,
      datasets: [
        {
          label: 'Impresiones',
          data: facebookInsights.page_impressions.values.map((v: any) => Number(v?.value) || 0),
          borderColor: '#1877F2',
          backgroundColor: 'rgba(24, 119, 242, 0.1)',
          fill: true
        },
        {
          label: 'Alcance',
          data: (facebookInsights.page_impressions_unique?.values || []).map((v: any) => Number(v?.value) || 0),
          borderColor: '#42B883',
          backgroundColor: 'rgba(66, 184, 131, 0.1)',
          fill: true
        }
      ]
    };
  }, [facebookInsights]);

  // Manejo de errores
  const handleRetry = useCallback(() => {
    if (company?.id) {
      const endDate = moment().format('YYYY-MM-DD');
      const startDate = moment().subtract(
        selectedPeriod === '7d' ? 7 : selectedPeriod === '30d' ? 30 : 90, 
        'days'
      ).format('YYYY-MM-DD');
      
      getFacebookInsights(company.id, startDate, endDate, 'Daily');
    }
  }, [company, selectedPeriod, getFacebookInsights]);

  const handleReconnect = useCallback(() => {
    // Redirigir a la página de configuración de Facebook
    window.location.href = '/social/facebook';
  }, []);

  const handleContactSupport = useCallback(() => {
    // Abrir modal de contacto o redirigir a página de soporte
    window.open('/contact', '_blank');
  }, []);

  const renderError = () => {
    // Detectar si hay métricas vacías o error 500
    const hasNoData = !facebookInsights || Object.keys(facebookInsights).length === 0;
    
    if (!hasNoData && !facebookErrorCode) return null;

    return (
      <Alert color="warning" className="mb-4">
        <div className="d-flex align-items-start">
          <FontAwesomeIcon icon={faExclamationTriangle} size="lg" className="me-3 mt-1" />
          <div className="flex-grow-1">
            <h5 className="alert-heading mb-2">
              <strong>Error al cargar las métricas de Facebook</strong>
            </h5>
            <p className="mb-2">
              El servidor de Facebook está rechazando algunas métricas solicitadas. 
              Esto puede deberse a:
            </p>
            <ul className="mb-3">
              <li>Métricas deprecadas por Facebook que ya no están disponibles</li>
              <li>Falta de permisos en la aplicación de Facebook</li>
              <li>Cambios recientes en la API de Facebook Insights</li>
              <li>La página de Facebook no tiene datos suficientes</li>
            </ul>
            <p className="mb-3">
              <strong>Error del servidor:</strong> 
              <code className="ms-2 bg-light px-2 py-1 rounded">
                (#100) The value must be a valid insights metric
              </code>
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <Button color="primary" size="sm" onClick={handleRetry}>
                <FontAwesomeIcon icon={faRefresh} className="me-1" />
                Reintentar
              </Button>
              <Button color="secondary" size="sm" onClick={handleReconnect}>
                <FontAwesomeIcon icon={faFacebook} className="me-1" />
                Reconectar Facebook
              </Button>
              <Button color="info" size="sm" onClick={handleContactSupport} outline>
                <FontAwesomeIcon icon={faLifeRing} className="me-1" />
                Contactar Soporte
              </Button>
            </div>
            <hr className="my-3" />
            <p className="mb-0 text-muted small">
              <strong>Nota técnica:</strong> Este es un error del backend. 
              El equipo de desarrollo debe revisar qué métricas de Facebook Insights 
              se están solicitando en el endpoint <code>/api/facebook/insights</code> 
              y actualizarlas según la documentación actual de Facebook Graph API.
            </p>
          </div>
        </div>
      </Alert>
    );
  };

  // Componente de tarjeta de métrica
  const MetricCard = ({ metric }: { metric: MetricCard }) => (
    <Card className="metric-card">
      <CardBody>
        <div className="metric-header">
          <div className="metric-icon" style={{ backgroundColor: metric.color }}>
            <FontAwesomeIcon icon={metric.icon} />
          </div>
          <div className="metric-info">
            <h6 className="metric-title">{metric.title}</h6>
            <h4 className="metric-value">{metric.value.toLocaleString()}</h4>
            {metric.change !== undefined && (
              <div className={`metric-change ${metric.changeType}`}>
                <FontAwesomeIcon 
                  icon={metric.changeType === 'increase' ? 'arrow-up' : 'arrow-down'} 
                  className="me-1"
                />
                {Math.abs(metric.change)}%
              </div>
            )}
          </div>
        </div>
        {metric.description && (
          <p className="metric-description">{metric.description}</p>
        )}
      </CardBody>
    </Card>
  );

  // Navegación de pestañas
  const tabs = [
    { id: 'overview', label: 'Visión General', icon: faChartLine },
    { id: 'demographics', label: 'Demografía', icon: faUserFriends },
    { id: 'clicks', label: 'Clics en la Página', icon: faMousePointer },
    { id: 'posts', label: 'Publicaciones', icon: faNewspaper },
    { id: 'stories', label: 'Historias', icon: faVideo },
    { id: 'competitors', label: 'Competidores', icon: faGlobe }
  ];

  if (!isFacebookConnected) {
    return (
      <Container>
        <Alert color="warning">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Facebook no conectado</strong>
          <br />
          Para ver las métricas de Facebook, primero debes conectar tu cuenta de Facebook.
        </Alert>
      </Container>
    );
  }

  return (
    <div className="facebook-metrics-dashboard">
      <Container fluid>
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <div className="page-title">
              <FontAwesomeIcon icon={faFacebook} className="me-2" />
              <h2>Estadísticas de Facebook</h2>
            </div>
          </div>
          <div className="header-right">
            <div className="period-selector">
              <button 
                className={`period-btn ${selectedPeriod === '7d' ? 'active' : ''}`}
                onClick={() => setSelectedPeriod('7d')}
              >
                Últimos 7 días
              </button>
              <button 
                className={`period-btn ${selectedPeriod === '30d' ? 'active' : ''}`}
                onClick={() => setSelectedPeriod('30d')}
              >
                Últimos 30 días
              </button>
              <button 
                className={`period-btn ${selectedPeriod === '90d' ? 'active' : ''}`}
                onClick={() => setSelectedPeriod('90d')}
              >
                Últimos 90 días
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {renderError()}

        {/* Navigation Tabs */}
        <div className="dashboard-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <FontAwesomeIcon icon={tab.icon} className="me-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading('FACEBOOK_INSIGHT_LOADING') && (
          <div className="loading-container">
            <Spinner color="primary" />
            <p>Cargando métricas de Facebook...</p>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && !isLoading('FACEBOOK_INSIGHT_LOADING') && (
          <>
            {/* Crecimiento Section */}
            <div className="metrics-section">
              <h3 className="section-title">Crecimiento</h3>
              <Row>
                {mainMetrics.map((metric, index) => (
                  <Col md={6} lg={4} xl={2.4} key={index}>
                    <MetricCard metric={metric} />
                  </Col>
                ))}
              </Row>
            </div>

            {/* Gráfico de Impresiones */}
            <div className="chart-section">
              <FacebookMetricsChart
                title="Impresiones"
                data={chartData}
                type="line"
                height={300}
              />
            </div>

            {/* Balance de Me gusta */}
            <div className="metrics-section">
              <FacebookLikesBalance 
                facebookInsights={facebookInsights}
                selectedPeriod={selectedPeriod}
              />
            </div>

            {/* Interacciones */}
            <div className="metrics-section">
              <FacebookInteractions 
                facebookInsights={facebookInsights}
                selectedPeriod={selectedPeriod}
              />
            </div>
          </>
        )}

        {/* Demographics Tab */}
        {activeTab === 'demographics' && (
          <div className="demographics-section">
            <Row>
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <h4>Seguidores por país</h4>
                  </CardHeader>
                  <CardBody>
                    <div className="chart-placeholder">
                      <FontAwesomeIcon icon={faGlobe} size="2x" />
                      <p>Gráfico de países</p>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <h4>Seguidores por ciudad</h4>
                  </CardHeader>
                  <CardBody>
                    <div className="chart-placeholder">
                      <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" />
                      <p>Gráfico de ciudades</p>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && !isLoading('FACEBOOK_INSIGHT_LOADING') && (
          <div className="posts-section">
            <FacebookPostsTable 
              facebookInsights={facebookInsights}
              selectedPeriod={selectedPeriod}
            />
          </div>
        )}

        {/* Other tabs would be implemented similarly */}
        {activeTab !== 'overview' && activeTab !== 'demographics' && activeTab !== 'posts' && (
          <div className="coming-soon">
            <FontAwesomeIcon icon={faChartLine} size="3x" />
            <h3>Próximamente</h3>
            <p>Esta sección estará disponible en una próxima actualización.</p>
          </div>
        )}
      </Container>
    </div>
  );
};
